import { Spinner, ToastContainer } from '@bloobirds-it/flamingo-ui';
import { useEventSubscription, usePlover } from '@bloobirds-it/plover';
import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import React, { useEffect } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { LogoSvg } from '../../../assets/svg';
import AddToCalendarModal from '../../components/addToCalendarModal';
import AppCalendar from '../../components/appCalendar/appCalendar';
import BobjectDetailsModal from '../../components/bobjectDetailsModal/bobjectDetailsModal';
import BobjectForm from '../../components/bobjectForm';
import { TableContextProvider } from '../../components/bobjectTable/context/bobjectTable.context';
import CadenceControlModal from '../../components/cadenceControlModal';
import Dialer from '../../components/dialer';
import DownloadConfirmationModal from '../../components/downloadConfirmationModal';
import DuplicateValidationModal from '../../components/duplicateValidationModal';
import ErrorBoundary from '../../components/errorBoundary';
import Header from '../../components/header/header';
import { JustCallDialer } from '../../components/justCallDialer/components/justCallDialer.view';
import MinimizableModals from '../../components/minimizableModals';
import { UserPermissionContext } from '../../components/userPermissions';
import { useUserSettings } from '../../components/userPermissions/hooks';
import ViewTagsModal from '../../components/viewTagsModal';
import {
  useActiveUser,
  useAddToCalendar,
  useBobjectDetails,
  useBobjectFormVisibility,
  useCadenceControl,
  useDuplicateValidationModal,
  useEntity,
} from '../../hooks';
import { AircallProvider } from '../../hooks/useAirCallDialerVisibility';
import { useAppCalendarVisibility } from '../../hooks/useUserTasks';
import { useBobjectsDownload } from '../../hooks/useBobjectsDownload';
import {
  useFeatureFlags,
  useOnboardingFlow,
} from '../../hooks/useFeatureFlags';
import { useLogin } from '../../hooks/useLogin';
import { usePingVersion } from '../../hooks/usePingVersion';
import { useReminders } from '../../hooks/useReminders';
import { useTagsModal } from '../../hooks/useTagsModal';
import { ApiHosts, servicesEnv } from '../../misc/api/ApiHosts';
import { useIntercom } from '../../misc/intercom/intercom';
import SessionManagerFactory from '../../misc/session';
import AccountSettings from '../../pages/accountSettingsPages';
import Dashboard from '../../pages/dashboardPages';
import PlaybookPage from '../../pages/playbookPages/playbookPage';
import UserSettings from '../../pages/userSettingsPages';
import * as Routes from '../_constants/routes';
import styles from './AuthenticatedScopeApp.module.css';
import Board from './board';
import NotFoundManager from './notFound';
import { api } from '../../utils/api';
import { useBobjectTypes } from '../../hooks/useBobjectTypes';
import useManageProducts from '../../hooks/useManageProducts';
import ManageProductsModal from '../../pages/contactPages/opportunityPage/manageProductsModal/manageProductsModal';
import { useRescheduleCadenceTask } from '../../components/rescheduleTask/useRescheduleCadenceTask';
import RescheduleCadenceTaskModal from '../../components/rescheduleTask/rescheduleCadenceTaskModal';
import MeetingModal from '../../components/newMeetingModal/meetingModal';
import useModalVisibility from '../../hooks/useModalVisibility';
import WelcomeScreens from '../../components/welcomeScreensModal/welcomeScreens';
import { useUserHelpers } from '../../hooks/useUserHelpers';
import { UserHelperKeys } from '../../constants/userHelperKeys';
import { useInactiveHandlingModal } from '../../components/inactiveHandlingModal/useInactiveHandlingModal';
import { InactiveHandlingModal } from '../../components/inactiveHandlingModal/inactiveHandlingModal';
import GeneralSearchBar from '../../components/generalSearchBar/generalSearchBar';
import { usePreviewEmailModal } from '../../hooks/usePreviewEmailModal';
import { PreviewEmailModalWrapper } from '../../components/previewEmailModal/previewEmailModalWrapper';
import { ConfirmDeleteModal } from '@bloobirds-it/bobjects';
import { usePreviousUrl, useRouter } from '@bloobirds-it/hooks';
import CadencesPage from '../../pages/cadencesPage/cadencesPage';

const SessionManager = SessionManagerFactory();

const withBasicWebsocketSubscriptions = Component => () => {
  const { setPingVersion } = usePingVersion();
  const { logout } = useLogin();
  const { history } = useRouter();

  useEventSubscription('Ping', data => {
    setPingVersion(data?.version);
  });

  useEventSubscription('logout', () => {
    logout({ onError: () => history.push(Routes.LOGIN) });
  });
  return <Component />;
};

const AuthenticatedScopeWrapper = ({ children }) => {
  const contextToken = SessionManager.getToken();
  const token = SessionManager.getRootToken();

  useEffect(() => {
    if (token !== contextToken) {
      SessionManager.updateToken(contextToken);
    }
  }, [token, contextToken]);

  return (
    <SWRConfig
      value={{
        fetcher: async url => {
          try {
            const response = await api.get(url);
            return response.data;
          } catch (error) {
            console.warn(error);
          }
        },
        revalidateOnFocus: false,
      }}
    >
      <ErrorBoundary>
        <UserPermissionContext>{children}</UserPermissionContext>
      </ErrorBoundary>
    </SWRConfig>
  );
};

const ApplicationModals = () => {
  const { isOpen: isAddToCalendarOpen } = useAddToCalendar();
  const { isOpen: isBobjectDetailsOpen } = useBobjectDetails();
  const { isOpen: isBobjectFormOpen } = useBobjectFormVisibility();
  const { isOpen: isDuplicateValidationOpen } = useDuplicateValidationModal();
  const { isModalOpen: isDownloadConfirmationModalOpen } = useBobjectsDownload();
  const { isOpen: isOpenMeetingModal } = useModalVisibility('meetingModal');
  const { isOpen: isOpenCadenceControl } = useCadenceControl();
  const { isOpen: isOpenCalendar } = useAppCalendarVisibility();
  const { isOpen: isRescheduleTaskModalOpen } = useRescheduleCadenceTask();
  const {
    modalState: { visible: isInactiveHandlingModalOpen },
  } = useInactiveHandlingModal();
  const {
    isOpen: isPreviewEmailModalOpen,
    handleClose: handlePreviewEmailClose,
  } = usePreviewEmailModal();
  const { openTagsModal } = useTagsModal();
  const {
    isOpen: isOpenManageProductsModal,
    closeModal: closeManageProductsModal,
  } = useManageProducts();
  const settings = useUserSettings();
  const {
    user: { dialerType },
  } = settings;
  const helpers = useUserHelpers();
  const hasCompletedWelcomeScreens = helpers.has(UserHelperKeys.COMPLETE_WELCOME_SCREEN);
  const { history, location } = useRouter();

  return (
    <>
      <ConfirmDeleteModal history={history} location={location} />
      <MinimizableModals />
      <GeneralSearchBar />
      {openTagsModal && <ViewTagsModal />}
      {isDownloadConfirmationModalOpen && <DownloadConfirmationModal />}
      {dialerType === 'JUST_CALL_DIALER' && <JustCallDialer />}
      {isDuplicateValidationOpen && <DuplicateValidationModal />}
      {isBobjectFormOpen && <BobjectForm />}
      {isBobjectDetailsOpen && <BobjectDetailsModal />}
      {isAddToCalendarOpen && <AddToCalendarModal />}
      {isOpenMeetingModal && <MeetingModal />}
      {isOpenCadenceControl && <CadenceControlModal />}
      {isRescheduleTaskModalOpen && <RescheduleCadenceTaskModal />}
      {isOpenCalendar && <AppCalendar />}
      {isPreviewEmailModalOpen && <PreviewEmailModalWrapper onClose={handlePreviewEmailClose} />}
      {isOpenManageProductsModal && (
        <ManageProductsModal open handleClose={closeManageProductsModal} />
      )}
      {!hasCompletedWelcomeScreens && <WelcomeScreens />}
      {isInactiveHandlingModalOpen && <InactiveHandlingModal />}
    </>
  );
};

const WebsocketComponent = React.memo(() => {
  usePlover(SessionManager.getToken(), ApiHosts.websocket.host(), false);
  return null;
});

const AuthenticatedScope = () => {
  const bobjectFields = useEntity('bobjectFields');
  const bobjectPicklistFieldValues = useEntity('bobjectPicklistFieldValues');
  const bobjectTypes = useBobjectTypes();
  const fieldTypes = useEntity('fieldTypes');
  useReminders();

  const { setUserActiveData } = useActiveUser();
  const account = SessionManager.getAccount();
  const user = SessionManager.getUser();
  const { resetPreviousUrl } = usePreviousUrl();
  const { featureFlags } = useFeatureFlags();
  const settings = useUserSettings();

  useEffect(() => {
    resetPreviousUrl();
  }, []);

  useEffect(() => {
    if (settings) {
      const userSettings = settings?.user;
      const accountSettings = settings?.settings;
      const accountProps = settings?.account;
      setUserActiveData(user, account);
      Sentry.setUser({ id: user?.id, username: user?.name, account: account?.id });
      window.zestSettings = {
        contact_name: user?.name,
        contact_email: user?.email,
        contact_id: user?.id,
      };

      sessionStorage.setItem('accountId', account?.id);
      if (servicesEnv === 'production') {
        mixpanel.identify(user?.id);
        mixpanel.people.set({
          $name: user?.name,
          $email: userSettings?.email,
          account_id: account?.id,
          account_name: account?.name,
          account_type: accountProps?.type,
          account_email_type: accountSettings?.mailToLinksType,
          account_open_calendar_pop_up: accountSettings?.openCalendarPopupAfterMeeting,
          dialer_default_view: userSettings?.dialerDefaultView,
          dialer_type: userSettings?.dialerType,
          employee_role: userSettings?.employeeRole,
          has_manual_call: userSettings?.enableLogCall,
          type: userSettings?.type,
          task_reminders_enabled: userSettings?.remindersEnabled,
          task_sound_reminders_enabled: userSettings?.remindersSoundEnabled,
          task_reminders_before_minutes: userSettings?.remindersBeforeMinutes,
          incoming_calls_forwarded: userSettings?.incomingCallsForwarding,
        });

        mixpanel.add_group('account', account?.name);
      }
    }
  }, [user, account, settings]);

  useIntercom();

  return (
    <>
      {fieldTypes && bobjectPicklistFieldValues && bobjectFields && bobjectTypes && featureFlags ? (
        <>
          <WebsocketComponent />
          <TableContextProvider>
            <AircallProvider>
              <Header />
              <div className={styles._content}>
                <Route exact path={Routes.APP} render={() => <Redirect to={Routes.APP_TASKS} />} />
                <Dialer />
                <Board />
                <Route
                  exact
                  path={Routes.APP_MANAGEMENT}
                  render={() => <Redirect to={Routes.APP_MANAGEMENT_USER} />}
                />
                <Route path={Routes.APP_MANAGEMENT_USER} component={UserSettings} />
                <Route path={Routes.APP_ACCOUNT} component={AccountSettings} />
                <Route path={Routes.APP_MANAGEMENT_ACCOUNT} component={AccountSettings} />
                <Route path={Routes.APP_CADENCES} component={CadencesPage} />
                <Route path={Routes.APP_PLAYBOOK} component={PlaybookPage} />
                <Route path={Routes.APP_DASHBOARD} component={Dashboard} />
                <NotFoundManager />
                <ApplicationModals />
                <ToastContainer />
              </div>
            </AircallProvider>
          </TableContextProvider>
        </>
      ) : (
        <div className={styles.loader}>
          <LogoSvg fill="var(--bloobirds)" width={50} />
          <div className={styles.spinner}>
            <Spinner name="loadingCircle" />
          </div>
        </div>
      )}
    </>
  );
};

const WebsocketSubscribedApp = withBasicWebsocketSubscriptions(AuthenticatedScope);

const AuthenticatedScopeApp = () => {
  return (
    <AuthenticatedScopeWrapper>
      <WebsocketSubscribedApp />
    </AuthenticatedScopeWrapper>
  );
};

export default AuthenticatedScopeApp;
